import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Search, Building2, BookOpen, ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { label: "University", icon: Building2 },
  { label: "Program", icon: BookOpen },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [unis, setUnis] = useState([]);
  const [majors, setMajors] = useState([]);
  const [query, setQuery] = useState("");
  const [uni, setUni] = useState(null);
  const [major, setMajor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetch(`${API}/universities`)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setUnis(list);
        // Deep entry: /onboarding?uni=ID jumps straight to Program selection
        const uniParam = new URLSearchParams(window.location.search).get("uni");
        if (uniParam) {
          const u = list.find((x) => x.external_id === uniParam);
          if (u) { setUni(u); setStep(1); }
        }
      })
      .catch(() => toast.error("Failed to load universities"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!uni) return;
    setMajors([]);
    fetch(`${API}/majors/by-university/${uni.external_id}`)
      .then((r) => r.json())
      .then((d) => setMajors(Array.isArray(d) ? d : []))
      .catch(() => toast.error("Failed to load programs"));
  }, [uni]);

  const filteredUnis = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return unis;
    return unis.filter((u) =>
      `${u.name} ${u.country || ""} ${u.city || ""}`.toLowerCase().includes(q));
  }, [unis, query]);


  const finish = async (selectedMajor) => {
    setSaving(true);
    const m = selectedMajor ?? major;
    localStorage.setItem("cm_last_uni", JSON.stringify({ id: uni.external_id, name: uni.name }));
    localStorage.setItem("cm_onboarding", JSON.stringify({
      university_id: uni.external_id, university_name: uni.name,
      major_id: m?.external_id || null, major_name: m?.name || null,
    }));
    const token = localStorage.getItem("session_token");
    if (token) {
      try {
        await fetch(`${API}/auth/preferences`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          credentials: "include",
          body: JSON.stringify({ university_id: uni.external_id, major_id: m?.external_id }),
        });
      } catch { /* saved locally anyway */ }
    }
    toast.success("You're all set!");
    navigate("/my-courses");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="onboarding-page">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200/60">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="onboarding-logo">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#0F2744]">
              <GraduationCap className="h-5 w-5 text-[#FF6B35]" />
            </div>
            <span className="text-xl font-extrabold text-[#1E3A5F]">Class<span className="text-[#2563EB]">Mate</span></span>
          </button>
          <button onClick={() => navigate("/home")} className="text-sm font-semibold text-slate-500 hover:text-[#2563EB]" data-testid="onboarding-skip-btn">
            Skip for now
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="flex items-center justify-center gap-0 mb-8" data-testid="onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={s.label} className="flex items-center">
              <div className="flex flex-col items-center">
                <span className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  i < step ? "bg-[#2563EB] border-[#2563EB] text-white"
                  : i === step ? "border-[#2563EB] text-[#2563EB] bg-[#2563EB]/5"
                  : "border-slate-200 text-slate-400"}`}>
                  {i < step ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </span>
                <span className={`mt-1.5 text-xs font-semibold ${i <= step ? "text-[#1E3A5F]" : "text-slate-400"}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-16 sm:w-24 h-0.5 mx-2 mb-5 ${i < step ? "bg-[#2563EB]" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: University */}
        {step === 0 && (
          <section data-testid="onboarding-step-university">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F2744] text-center" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Where do you study?
            </h1>
            <p className="mt-2 text-sm text-slate-500 text-center">Pick your university — we'll show you your exact courses.</p>
            <div className="relative mt-5 max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search university, country or city…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none text-sm"
                data-testid="onboarding-uni-search"
              />
            </div>
            {loading ? (
              <p className="text-center text-slate-400 mt-8">Loading universities…</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2.5 mt-5" data-testid="onboarding-uni-list">
                {filteredUnis.map((u) => (
                  <button key={u.external_id}
                          onClick={() => { setUni(u); setMajor(null); setStep(1); }}
                          className="flex items-center gap-3 rounded-xl border border-slate-200 hover:border-[#2563EB] hover:bg-[#2563EB]/5 p-3.5 text-left transition-colors"
                          data-testid={`uni-option-${u.external_id}`}>
                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#2563EB]/10 shrink-0">
                      <Building2 className="h-5 w-5 text-[#2563EB]" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-bold text-[#0F2744] text-sm leading-snug">{u.name}</span>
                      <span className="block text-xs text-slate-500 truncate">{[u.city, u.country].filter(Boolean).join(", ")}{u.course_count ? ` · ${u.course_count} courses` : ""}</span>
                    </span>
                  </button>
                ))}
                {filteredUnis.length === 0 && (
                  <p className="col-span-2 text-center text-slate-400 py-6" data-testid="onboarding-no-results">No universities match your search.</p>
                )}
              </div>
            )}
          </section>
        )}

        {/* Step 2: Program */}
        {step === 1 && uni && (
          <section data-testid="onboarding-step-program">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F2744] text-center" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              What are you studying?
            </h1>
            <p className="mt-2 text-sm text-slate-500 text-center">{uni.name}</p>
            <div className="grid sm:grid-cols-2 gap-2.5 mt-6 max-w-xl mx-auto" data-testid="onboarding-program-list">
              {majors.map((m) => (
                <button key={m.external_id}
                        onClick={() => { setMajor(m); setSavingId(m.external_id); finish(m); }}
                        disabled={saving}
                        className="flex items-center gap-3 rounded-xl border border-slate-200 hover:border-[#2563EB] hover:bg-[#2563EB]/5 p-4 text-left transition-colors disabled:opacity-60"
                        data-testid={`program-option-${m.external_id}`}>
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#2563EB]/10 shrink-0">
                    {savingId === m.external_id ? (
                      <span className="h-5 w-5 rounded-full border-2 border-[#2563EB] border-b-transparent animate-spin" />
                    ) : (
                      <BookOpen className="h-5 w-5 text-[#2563EB]" />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-bold text-[#0F2744] text-sm">{savingId === m.external_id ? "Opening your courses…" : m.name}</span>
                    <span className="block text-xs text-slate-500">{[m.degree, m.years ? `${m.years} years` : null, m.course_count ? `${m.course_count} courses` : null].filter(Boolean).join(" · ")}</span>
                  </span>
                </button>
              ))}
              {majors.length === 0 && <p className="col-span-2 text-center text-slate-400 py-6">Loading programs…</p>}
            </div>
            <div className="flex justify-center mt-6">
              <button onClick={() => setStep(0)} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#2563EB]" data-testid="onboarding-back-to-uni">
                <ChevronLeft className="h-4 w-4" /> Change university
              </button>
            </div>
          </section>
        )}

        {/* Step 3 (Year) removed — year/semester are optional filters inside My Courses */}
      </main>
    </div>
  );
}
